import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Plus } from 'lucide-react';

import {
  UserStatsCards,
  UserFilters,
  UsersTable,
  UserFormModal,
  RolesTypesModal,
  UserProfileModal,
  WalletAdjustmentModal,
  DeactivateUserModal,
  isStaffRole
} from '../../components/admin/users';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AdminUsers() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [managerModules, setManagerModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Wallet adjustment
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletAdjustment, setWalletAdjustment] = useState({
    user_id: '',
    user_name: '',
    current_balance: 0,
    amount: '',
    reason: '',
    admin_password: ''
  });
  const [isAdjusting, setIsAdjusting] = useState(false);

  // Deactivation dialog
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivatingUser, setDeactivatingUser] = useState(null);
  const [deactivationReason, setDeactivationReason] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);

  // Stats
  const [stats, setStats] = useState({ all: 0, customer: 0, provider: 0, admin: 0, manager: 0, customer_service: 0, accountant: 0 });

  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'customer',
    is_active: true,
    permissions: {}
  });

  const [typeForm, setTypeForm] = useState({
    name: '',
    slug: '',
    description: '',
    permissions: [],
    is_active: true
  });
  const [roleDefaults, setRoleDefaults] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const openWalletAdjustment = async (userId, userName) => {
    try {
      const res = await axios.get(`${API}/admin/wallets/${userId}`, { headers });
      setWalletAdjustment({
        user_id: userId,
        user_name: userName,
        current_balance: res.data.balance || 0,
        amount: '',
        reason: '',
        admin_password: ''
      });
      setShowWalletModal(true);
    } catch (error) {
      toast.error('Failed to fetch wallet info');
    }
  };

  const handleWalletAdjustment = async () => {
    if (!walletAdjustment.amount || !walletAdjustment.reason || !walletAdjustment.admin_password) {
      toast.error('Please fill all fields');
      return;
    }

    setIsAdjusting(true);
    try {
      const res = await axios.post(`${API}/admin/wallets/adjust`, {
        user_id: walletAdjustment.user_id,
        amount: Number(walletAdjustment.amount),
        reason: walletAdjustment.reason,
        admin_password: walletAdjustment.admin_password
      }, { headers });

      toast.success(`Wallet adjusted: ₱${res.data.old_balance.toLocaleString()} → ₱${res.data.new_balance.toLocaleString()}`);
      setShowWalletModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust wallet');
    } finally {
      setIsAdjusting(false);
    }
  };

  const fetchUserProfile = async (userId) => {
    setLoadingProfile(true);
    setShowProfileModal(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/profile`, { headers });
      setSelectedUserProfile(res.data);
    } catch (error) {
      toast.error('Failed to load user profile');
      setShowProfileModal(false);
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(`${API}/admin/users?${params}`, { headers });
      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [token, page, limit, roleFilter, statusFilter, searchTerm, headers]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const [allRes, customerRes, providerRes, adminRes, managerRes, csRes, acctRes, typesRes, modulesRes] = await Promise.all([
        axios.get(`${API}/admin/users?limit=1`, { headers }),
        axios.get(`${API}/admin/users?role=customer&limit=1`, { headers }),
        axios.get(`${API}/admin/users?role=provider&limit=1`, { headers }),
        axios.get(`${API}/admin/users?role=admin&limit=1`, { headers }),
        axios.get(`${API}/admin/users?role=manager&limit=1`, { headers }),
        axios.get(`${API}/admin/users?role=customer_service&limit=1`, { headers }),
        axios.get(`${API}/admin/users?role=accountant&limit=1`, { headers }),
        axios.get(`${API}/admin/user-types`, { headers }),
        axios.get(`${API}/admin/manager-modules`, { headers })
      ]);
      setStats({
        all: allRes.data.total || 0,
        customer: customerRes.data.total || 0,
        provider: providerRes.data.total || 0,
        admin: adminRes.data.total || 0,
        manager: managerRes.data.total || 0,
        customer_service: csRes.data.total || 0,
        accountant: acctRes.data.total || 0
      });
      setUserTypes(typesRes.data || []);
      setManagerModules(modulesRes.data || []);

      const defaultPerms = {};
      (modulesRes.data || []).forEach(mod => {
        defaultPerms[mod.id] = { can_view: false, can_add: false, can_edit: false, can_delete: false };
      });
      setUserForm(prev => ({ ...prev, permissions: defaultPerms }));
      setDataLoaded(true);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token, headers]);

  useEffect(() => {
    if (token) {
      fetchStats();
    }
  }, [token, fetchStats]);

  useEffect(() => {
    if (token && dataLoaded) {
      fetchUsers();
    }
  }, [token, dataLoaded, page, roleFilter, statusFilter, fetchUsers]);

  // Debounced search
  useEffect(() => {
    if (!token || !dataLoaded) return;
    const timer = setTimeout(() => {
      if (page !== 1) setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, token, dataLoaded, page]);

  const handleSaveUser = async () => {
    setIsSaving(true);
    try {
      if (editingUser) {
        if (isStaffRole(userForm.role)) {
          await axios.put(`${API}/admin/staff/${editingUser.id}`, {
            name: userForm.name,
            permissions: userForm.permissions,
            is_active: userForm.is_active,
          }, { headers });
        } else {
          await axios.put(`${API}/admin/users/${editingUser.id}`, userForm, { headers });
        }
        toast.success('User updated!');
      } else {
        if (isStaffRole(userForm.role)) {
          await axios.post(`${API}/admin/staff`, {
            name: userForm.name,
            email: userForm.email,
            password: userForm.password,
            role: userForm.role,
            permissions: userForm.permissions,
          }, { headers });
        } else {
          await axios.post(`${API}/admin/users`, userForm, { headers });
        }
        toast.success('User created!');
      }
      setShowUserModal(false);
      setEditingUser(null);
      resetUserForm();
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus, userName = '') => {
    if (currentStatus) {
      setDeactivatingUser({ id: userId, name: userName });
      setDeactivationReason('');
      setShowDeactivateModal(true);
      return;
    }

    try {
      await axios.put(`${API}/admin/users/${userId}/status?is_active=true`, {}, { headers });
      toast.success('User activated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleConfirmDeactivation = async () => {
    if (!deactivationReason.trim()) {
      toast.error('Please provide a reason for deactivation');
      return;
    }

    try {
      await axios.put(
        `${API}/admin/users/${deactivatingUser.id}/status?is_active=false&reason=${encodeURIComponent(deactivationReason)}`,
        {},
        { headers }
      );
      toast.success('User deactivated');
      setShowDeactivateModal(false);
      setDeactivatingUser(null);
      setDeactivationReason('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast.success('User deleted');
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleRestoreUser = async (userId, userName = '') => {
    if (!window.confirm(`Restore ${userName || 'this user'}? They will be able to sign in again.`)) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/restore`, {}, { headers });
      toast.success('User restored');
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore user');
    }
  };

  const handleSaveUserType = async () => {
    setIsSaving(true);
    try {
      const params = new URLSearchParams({
        name: typeForm.name,
        slug: typeForm.slug,
        description: typeForm.description || ''
      });
      if (editingType) {
        await axios.put(`${API}/admin/user-types/${editingType.id}?${params}`, {}, { headers });
        toast.success('User type updated!');
      } else {
        await axios.post(`${API}/admin/user-types?${params}`, {}, { headers });
        toast.success('User type created!');
      }
      setShowTypeModal(false);
      setEditingType(null);
      resetTypeForm();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user type');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUserType = async (typeId) => {
    if (!window.confirm('Delete this user type?')) return;
    try {
      await axios.delete(`${API}/admin/user-types/${typeId}`, { headers });
      toast.success('User type deleted');
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetUserForm = () => {
    const defaultPerms = {};
    managerModules.forEach(mod => {
      defaultPerms[mod.id] = { can_view: false, can_add: false, can_edit: false, can_delete: false };
    });
    setUserForm({ name: '', email: '', phone: '', password: '', role: 'customer', is_active: true, permissions: defaultPerms });
  };

  const resetTypeForm = () => {
    setTypeForm({ name: '', slug: '', description: '', permissions: [], is_active: true });
  };

  const openEditUser = (user) => {
    const perms = {};
    managerModules.forEach(mod => {
      perms[mod.id] = user.permissions?.[mod.id] || { can_view: false, can_add: false, can_edit: false, can_delete: false };
    });
    setUserForm({
      name: user.name,
      email: user.email || '',
      phone: user.phone || '',
      password: '',
      role: user.role,
      is_active: user.is_active,
      permissions: perms
    });
    setEditingUser(user);
    setShowUserModal(true);
  };

  const openManageTypes = async () => {
    setEditingType(null);
    resetTypeForm();
    setShowTypeModal(true);
    try {
      const res = await axios.get(`${API}/admin/staff/roles/defaults`, { headers });
      setRoleDefaults(res.data);
    } catch (e) {
      console.error('Failed to fetch role defaults');
    }
  };

  const handleCreateStaffFromRole = (role) => {
    setShowTypeModal(false);
    setEditingUser(null);
    const defaultPerms = {};
    managerModules.forEach(mod => {
      defaultPerms[mod.id] = role.default_permissions?.[mod.id] || { can_view: false, can_add: false, can_edit: false, can_delete: false };
    });
    setUserForm({ name: '', email: '', phone: '', password: '', role: role.role, is_active: true, permissions: defaultPerms });
    setShowUserModal(true);
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />

      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="w-10 h-10 rounded-full hover:bg-stone-200 flex items-center justify-center md:hidden"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 font-[Poppins]">User Management</h1>
              <p className="text-sm text-slate-500">Manage all users and user types</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button variant="outline" size="sm" onClick={openManageTypes} data-testid="manage-types-btn">
              <span className="hidden sm:inline">Manage Roles & Types</span>
              <span className="sm:hidden">Roles</span>
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setEditingUser(null);
                resetUserForm();
                setShowUserModal(true);
              }}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              data-testid="add-user-btn"
            >
              <Plus className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Add User</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>

        <UserStatsCards
          stats={stats}
          roleFilter={roleFilter}
          onFilterChange={(role) => { setRoleFilter(role); setPage(1); }}
        />

        <UserFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusChange={(status) => { setStatusFilter(status); setPage(1); }}
        />

        <UsersTable
          users={users}
          isLoading={isLoading}
          onView={fetchUserProfile}
          onEdit={openEditUser}
          onToggleStatus={handleToggleUserStatus}
          onDelete={handleDeleteUser}
          onRestore={handleRestoreUser}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />

        <UserFormModal
          open={showUserModal}
          onOpenChange={setShowUserModal}
          editingUser={editingUser}
          userForm={userForm}
          setUserForm={setUserForm}
          managerModules={managerModules}
          isSaving={isSaving}
          onSave={handleSaveUser}
          onCancel={() => {
            setShowUserModal(false);
            setEditingUser(null);
            resetUserForm();
          }}
        />

        <RolesTypesModal
          open={showTypeModal}
          onOpenChange={setShowTypeModal}
          roleDefaults={roleDefaults}
          userTypes={userTypes}
          typeForm={typeForm}
          setTypeForm={setTypeForm}
          editingType={editingType}
          isSaving={isSaving}
          onSaveType={handleSaveUserType}
          onCreateStaffFromRole={handleCreateStaffFromRole}
          onEditType={(type) => { setEditingType(type); setTypeForm(type); }}
          onDeleteType={handleDeleteUserType}
        />

        <UserProfileModal
          open={showProfileModal}
          onOpenChange={setShowProfileModal}
          profile={selectedUserProfile}
          loading={loadingProfile}
          backendUrl={BACKEND_URL}
          onAdjustWallet={(id, name) => {
            setShowProfileModal(false);
            openWalletAdjustment(id, name);
          }}
        />

        <WalletAdjustmentModal
          open={showWalletModal}
          onOpenChange={setShowWalletModal}
          walletData={walletAdjustment}
          onWalletDataChange={setWalletAdjustment}
          onSubmit={handleWalletAdjustment}
          isLoading={isAdjusting}
        />

        <DeactivateUserModal
          open={showDeactivateModal}
          onOpenChange={setShowDeactivateModal}
          user={deactivatingUser}
          reason={deactivationReason}
          onReasonChange={setDeactivationReason}
          onConfirm={handleConfirmDeactivation}
          isLoading={false}
        />
      </main>
    </div>
  );
}
